import { fetchAllPages } from '@frontend/api-utils';
import { Colli } from '@frontend/colli/types';
import { PackageClient } from '@frontend/package/api';
import { Package } from '@frontend/package/types';
import { useEffect, useMemo, useState } from 'react';

import { TransactionDetail } from '../transaction-detail-modal.controller';
import { ProofOfIdentityColliGroup, getPackagesRequiringProof, groupPackagesByColli, shouldGroupByColli } from './proof-of-identity-packages.utils';

export interface UseProofOfIdentityCardResult {
    isLoading: boolean;
    shouldShow: boolean;
    packagesRequiringProof: Package[];
    groupByColli: boolean;
    groups: ProofOfIdentityColliGroup[];
}

export function useProofOfIdentityCard(transaction: TransactionDetail | null, collis: Colli[] | null): UseProofOfIdentityCardResult {
    const [packages, setPackages] = useState<Package[] | null>(null);

    useEffect(() => {
        if (!transaction) {
            setPackages(null);
            return;
        }
        let cancelled = false;
        setPackages(null);
        fetchAllPages((queryParams) =>
            PackageClient.fetchPackages({
                ...queryParams,
                transaction_id: transaction.id,
                account_id: transaction.account_id
            })
        )
            .then((results) => {
                if (!cancelled) setPackages(results);
            })
            .catch(() => {
                if (!cancelled) setPackages([]);
            });
        return () => {
            cancelled = true;
        };
    }, [transaction]);

    const packagesRequiringProof = useMemo(() => getPackagesRequiringProof(packages ?? []), [packages]);

    const colliCount = collis?.length ?? 0;
    const groupByColli = shouldGroupByColli(colliCount);
    const groups = useMemo(() => {
        if (!groupByColli) return [];
        return groupPackagesByColli(packagesRequiringProof, collis ?? []);
    }, [groupByColli, packagesRequiringProof, collis]);

    const isLoading = packages === null || (packagesRequiringProof.length > 0 && collis === null);
    const shouldShow = packages !== null && collis !== null && packagesRequiringProof.length > 0;

    return {
        isLoading,
        shouldShow,
        packagesRequiringProof,
        groupByColli,
        groups
    };
}
